// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	ReactNode,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	Entity,
	EntityLoanPayload,
	EntityPayload,
	EntityType,
	ErrorCause,
	Feedback,
	Loan,
	Metadata,
} from '../../../@types';
import { entityTypesReadOnly } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Button, Checkbox, FormControlLabel, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { InputPhone } from '../../fields/InputPhone/InputPhone';
import { Select } from '../../fields/Select/Select';
import { SelectCountry } from '../../fields/SelectCountry/SelectCountry';

// forms
import { RepeatableFields } from './RepeatableFields/RepeatableFields';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// uitls
import { getOptionsEntityTypes } from '../../../utils/options';

// props
export interface FormManageEntityValues {
	files: File[];
	isKycVerified: boolean;
	'metadata-address1': string;
	'metadata-address2': string;
	'metadata-city': string;
	'metadata-country': string;
	'metadata-postcode': string;
	'metadata-region': string;
	name: string;
	phone: string;
	type: string;
}

export interface FormManageEntityProps {
	entity?: Entity;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	type?: EntityType;
}

export const FormManageEntity = ({
	entity,
	onClose,
	onSuccess,
	setIsLoading,
	type,
}: FormManageEntityProps) => {
	// hooks
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// state
	const [entities, setEntities] = useState<Entity[]>([]);
	const [entityLoans, setEntityLoans] = useState<Loan[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [sources, setSources] = useState(1);
	const [values, setValues] = useState<FormManageEntityValues>({
		files: [],
		isKycVerified: false,
		'metadata-address1': '',
		'metadata-address2': '',
		'metadata-city': '',
		'metadata-country': project?.country?.code || '',
		'metadata-postcode': '',
		'metadata-region': '',
		name: '',
		phone: '',
		type: 'PIU',
	});

	// vars
	const idForm = `form-manage-entity`;
	const parentRequiredTypes = ['INTERMEDIARY', 'SUB_PIU', 'SUPPLIER'];
	const isEdit = Boolean(entity);
	const isParentRequired = parentRequiredTypes.includes(type as string);
	const isEntityTypeReadOnly = entityTypesReadOnly.some((type) => type === values.type);

	// fetch loans
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code,status`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch entities
	let entitiesUrl = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=INTERMEDIARY,PIU,SUB_PIU`;
	if (entity) {
		entitiesUrl += `&parent=!${entity.id}`;
	}
	useFetch({
		isEnabled: Boolean(project),
		url: entitiesUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setEntities(res.data);
		},
	});

	// fetch entity loans
	useFetch({
		isEnabled: Boolean(entity),
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${entity?.id}&project=${project?.id}&fields=id,code,entities,status&populate=entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntityLoans(res.data);
				setSources(res.data.length);
			}
		},
	});

	const optionsTypes = getOptionsEntityTypes(t);

	const optionsLoans = loans.map((loan) => {
		return {
			disabled: loan.status === 'DISABLED',
			label: loan.code,
			value: String(loan.id),
		};
	});

	const optionsEntities = entities
		.filter((e) => e.id !== entity?.id)
		.map((entity) => {
			return {
				label: entity.name,
				value: String(entity.id),
			};
		});

	const renderSources = () => {
		// waiting until entity and entity loans exist
		// prevents changing default value of uncontrolled inputs
		if (!entity || (entity && entityLoans.length)) {
			const items: ReactNode[] = [];

			// for each source
			for (let i = 0; i < sources; i++) {
				// define default values for fields
				let loanDefaultValue = '';
				let loanLabel = null;
				let parentDefaultValue = '';
				let parentLabel = null;

				// define labels for first set of fields
				if (i === 0) {
					loanLabel = t('financingSource');
					parentLabel = t('parentEntity');
				}

				// define default loan value
				if (entityLoans?.[i]) {
					loanDefaultValue = String(entityLoans?.[i]?.id);
				}

				// define default parent entity value
				if (entityLoans?.[i]?.entities?.[0]?.parent?.id) {
					parentDefaultValue = String(entityLoans?.[i]?.entities?.[0]?.parent?.id);
				}

				items.push(
					<Grid item key={`source-${i}`} xs={12}>
						<RepeatableFields
							ButtonDeleteProps={{
								'aria-label': t('delete'),
								disabled: i === 0,
								onClick: () => setSources(sources - 1),
							}}
							entity={entity}
							entityType={values.type}
							SelectLoanProps={{
								defaultValue: loanDefaultValue,
								id: `form-manage-entity-source-${i}-loan`,
								label: loanLabel,
								name: `source-${i}-loan`,
								options: optionsLoans,
								required: true,
							}}
							SelectParentProps={{
								defaultValue: parentDefaultValue,
								disabled: isEntityTypeReadOnly,
								id: `form-manage-entity-source-${i}-parent`,
								label: parentLabel,
								name: `source-${i}-parent`,
								options: optionsEntities,
								required: isParentRequired,
							}}
						/>
					</Grid>
				);
			}

			return (
				<>
					{items}
					<Grid item xs={12}>
						<Button
							disabled={sources === loans.length}
							fullWidth={true}
							onClick={() => setSources(sources + 1)}
							variant="outlined">
							{t('addFinancingSource')}
						</Button>
					</Grid>
				</>
			);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// define form data
		const form = document.querySelector('#form-manage-entity') as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const metadata: Metadata = {};

		// populate metadata
		Object.keys(values).forEach((key, i) => {
			const value = values[key as keyof typeof values];
			if (key.includes('metadata-')) {
				// group metadata
				const resKey = key.replace('metadata-', '');
				metadata[resKey] = String(value);
			}
		});

		try {
			// clear error
			setFeedback(null);

			// set loading
			setIsLoading(true);

			// set associated entity
			const idParent = Number(entries.parent);

			if (!project) {
				throw new Error('The project does not exist.', {
					cause: {
						id: 'PROJECT_NON_EXISTENT',
					},
				});
			}

			if (!idParent && isParentRequired) {
				throw new Error('Associated parent entity must be selected.', {
					cause: {
						id: 'ENTITY_PARENT_NON_EXISTENT',
					},
				});
			}

			if (entries.loan === '') {
				throw new Error('Financing source must be selected.', {
					cause: {
						id: 'FINANCING_SOURCE_NOT_SELECTED',
					},
				});
			}

			// construct loans array
			const loans: EntityLoanPayload[] = [];

			// for each source
			for (let i = 0; i < sources; i++) {
				// define loan
				const loan = {
					id: Number(entries[`source-${i}-loan`]),
					parent: entries[`source-${i}-parent`] ? Number(entries[`source-${i}-parent`]) : null,
				};

				// check if loan exists
				const loanFound = loans.find((l) => l.id === loan.id);

				if (loanFound) {
					// get option of found loan
					const loanFoundOption = optionsLoans.find((l) => Number(l.value) === loan.id);
					throw new Error(`The loan ${loanFoundOption?.label} can only be selected once.`, {
						cause: {
							id: 'ENTITY_LOANS_IDENTICAL',
						},
					});
				} else {
					// push loan
					loans.push(loan);
				}
			}

			let url = `${process.env.REACT_APP_API_URL}/entities`;

			if (entity) {
				url += `/${entity.id}?project=${project.id}`;
			}

			const body: Partial<EntityPayload> = {
				name: String(values.name),
				metadata,
				loans,
				type: values.type as EntityType,
			};

			if (!isEdit) {
				body.isKycVerified = Boolean(values.isKycVerified);
				body.project = project.id;
			}

			if (values.phone) {
				body.phone = Number(values.phone);
			}

			const reqPostPatchEntity = await fetch(url, {
				method: isEdit ? 'PATCH' : 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resPostPatchEntity = await reqPostPatchEntity.json();

			if (resPostPatchEntity.error) {
				throw new Error(resPostPatchEntity.error.message, {
					cause: {
						details: resPostPatchEntity.error?.details,
						id: resPostPatchEntity.error.id,
						res: resPostPatchEntity,
					},
				});
			}

			const resEntity = resPostPatchEntity.data;

			// add documents using files
			if (!isEdit) {
				for (let i = 0; i < values.files.length; i++) {
					// define form data
					const formData = new FormData();
					const dateToday = new Date();
					const year = dateToday.getFullYear();
					const month = new Intl.DateTimeFormat('en-US', {
						month: '2-digit',
					}).format(dateToday);

					// append form data
					formData.append(
						'path',
						`bldt/projects/${project?.id}/entities/${resEntity.id}/${year}/${month}/`
					);
					formData.append('file', values.files[i]);
					formData.append('entities[]', String(resEntity.id));

					if (project) {
						formData.append('project', String(project.id));
					}

					// POST document
					const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});

					const resPostDoc = await fetchPostDoc.json();

					if (resPostDoc.error) {
						throw new Error(resPostDoc.error.message, {
							cause: {
								details: resPostDoc.error?.details,
								id: resPostDoc.error.id,
								res: resPostDoc,
							},
						});
					}
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			let bannerMessage = t('alert.entityCreated', { name: values.name });

			if (isEdit) {
				bannerMessage = t('alert.entityUpdated', { name: values.name });
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: bannerMessage,
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'ENTITY_INVALID_TYPE') {
					feedbackNew.alert.children = t('error.entityInvalidType', { type: values.type });
				}

				if (cause.id === 'ENTITY_LOANS_IDENTICAL') {
					feedbackNew.alert.children = t('error.entityLoansIdentical');
				}

				if (cause.id === 'ENTITY_UNAUTHORIZED_PATCH' || cause.id === 'ENTITY_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}

				if (cause.id === 'ENTITY_PARENT_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.associatedEntityNotSelected');
				}

				if (cause.id === 'FINANCING_SOURCE_NOT_SELECTED') {
					feedbackNew.alert.children = t('error.financingSourceNotSelected');
				}

				// store full error response for debugging
				if (cause.res) {
					feedbackNew.response = cause.res;
				}
			}

			// set error
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (entity) {
			const entityMetadata = entity?.metadata as Metadata;

			setValues((valuesPrev) => {
				return {
					...valuesPrev,
					isKycVerified: entity.isKycVerified,
					'metadata-address1': String(entityMetadata?.address1 || ''),
					'metadata-address2': String(entityMetadata?.address2 || ''),
					'metadata-city': String(entityMetadata?.city || ''),
					'metadata-country': String(entityMetadata?.country || ''),
					'metadata-postcode': String(entityMetadata?.postcode || ''),
					'metadata-region': String(entityMetadata?.region || ''),
					name: String(entity.name || ''),
					type: String(entity.type),
					phone: String(entity.phone || ''),
				};
			});
		}
	}, [entity]);

	return (
		<form id={idForm} name="formAddEntity" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-type`}
						label={t('type')}
						name="type"
						onChange={handleChangeValue}
						options={optionsTypes}
						required={true}
						value={values.type}
					/>
				</Grid>

				{renderSources()}
				<Grid item xs={12}>
					<Input
						id={`${idForm}-address1`}
						label={t('address')}
						name="metadata-address1"
						onChange={handleChangeValue}
						required={true}
						value={values['metadata-address1']}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-address2`}
						name="metadata-address2"
						onChange={handleChangeValue}
						value={values['metadata-address2']}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-city`}
						label={t('city')}
						name="metadata-city"
						onChange={handleChangeValue}
						required={true}
						value={values['metadata-city']}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<SelectCountry
						id={`${idForm}-country`}
						label={t('country')}
						name="metadata-country"
						onChange={handleChangeValue}
						required={true}
						value={values['metadata-country']}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-region`}
						label={t('region')}
						name="metadata-region"
						onChange={handleChangeValue}
						value={values['metadata-region']}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-postcode`}
						label={t('postcode')}
						name="metadata-postcode"
						onChange={handleChangeValue}
						value={values['metadata-postcode']}
					/>
				</Grid>
				<Grid item xs={12}>
					<InputPhone
						InputProps={{
							id: `${idForm}-phone`,
							name: 'phone',
							onChange: handleChangeValue,
							value: values.phone,
						}}
						SelectProps={{
							disabled: true,
							id: `${idForm}-country-code`,
							name: 'countryCode',
							onChange: handleChangeValue,
							value: values['metadata-country'],
							valueType: 'CODE',
						}}
						label={t('phone')}
					/>
				</Grid>
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							setValues((valuesPrev) => {
								return {
									...valuesPrev,
									files: filesSelected,
								};
							});
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={values.isKycVerified}
								disabled={Boolean(entity)}
								id={`${idForm}-is-kyc-verified`}
								name="isKycVerified"
								onChange={() => {
									setValues((valuesPrev) => {
										return {
											...valuesPrev,
											isKycVerified: !valuesPrev.isKycVerified,
										};
									});
								}}
								required={true}
							/>
						}
						label={t('formAddEntity.kycAml')}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
